import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment, Node as PMNode, Slice } from '@tiptap/pm/model';
import { ReplaceStep } from '@tiptap/pm/transform';

import insertLandscapeSectionCommand from './commands/insertLandscapeSectionCommand';
import unwrapSectionCommand from './commands/unwrapSectionCommand';
import unwrapAllSectionsCommand from './commands/unwrapAllSectionsCommand';
import deleteSectionCommand from './commands/deleteSectionCommand';
import SectionNodeComponent from './SectionNodeComponent';
import { markSystemTr } from '../../plugins/shared';
import { SECTION_CLASS_NAME, SECTION_HTML_SELECTOR, SECTION_NODE_NAME } from './constants';

// Plugin key for section manager (tracks section count + handles auto-merge)
export const sectionManagerKey = new PluginKey<{ sectionCount: number }>('section-manager');

/** Meta type for section count delta updates */
export type TSectionMeta = { delta?: number; forceRecount?: boolean };

/**
 * Get the current section count from editor state.
 * Used by auto-resize feature to determine container width.
 */
export const getSectionCount = (state: EditorState): number => {
  return sectionManagerKey.getState(state)?.sectionCount ?? 0;
};

declare module '@tiptap/core' {
  interface Storage {
    section: { isLandscape: boolean };
  }

  interface Commands<ReturnType> {
    section: {
      /** Insert a new landscape section at the cursor position */
      insertLandscapeSection: () => ReturnType;
      /** Unwrap content from section at the given position */
      unwrapSection: (pos: number) => ReturnType;
      /** Delete section at the given position */
      deleteSection: (pos: number) => ReturnType;
      /** Unwrap all sections in the document (for mode switching) */
      unwrapAllSections: () => ReturnType;
      /** Set document landscape mode in storage (triggers toolbar update) */
      setLandscapeMode: (isLandscape: boolean) => ReturnType;
    };
  }
}

/**
 * Count sections in document root. O(k) where k = root children count.
 * Used for initial count and fallback when delta tracking is unavailable.
 */
const countSections = (doc: PMNode): number => {
  let count = 0;
  for (let i = 0; i < doc.childCount; i++) {
    if (doc.child(i).type.name === 'section') count++;
  }
  return count;
};

/**
 * Find the root index at a given document position.
 * Returns the index of the root child containing the position.
 */
const getRootIndexAtPos = (doc: PMNode, pos: number): number => {
  let currentPos = 0;
  for (let i = 0; i < doc.childCount; i++) {
    const child = doc.child(i);
    if (pos >= currentPos && pos < currentPos + child.nodeSize) {
      return i;
    }
    currentPos += child.nodeSize;
  }
  return doc.childCount - 1;
};

/**
 * SectionNode - Container for landscape content in a portrait document.
 *
 * Portrait is the default document mode (no wrapper needed).
 * Landscape sections use this node to mark content that should be
 * displayed wider and exported as landscape pages.
 *
 * Uses a React NodeView to render a three-dots dropdown menu
 * for section actions (unwrap, delete) in the top-right corner.
 *
 * Performance notes:
 * - Section count uses delta-based tracking via meta for O(1) updates from commands
 * - Auto-merge only checks near changed positions, not entire document
 * - Falls back to O(k) recount for external changes (paste, collaborative editing)
 */
const SectionNode = Node.create({
  name: SECTION_NODE_NAME,
  // 'rootOnly' is a custom group for nodes that can only appear at document root.
  // useOptions.hook.tsx extends the Document root to accept it, with or without a title.
  group: 'rootOnly',
  content: 'block+',
  defining: true,
  isolating: true,
  draggable: false,
  selectable: false,

  addStorage() {
    return { isLandscape: false };
  },

  parseHTML() {
    return [{ tag: SECTION_HTML_SELECTOR }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['section', mergeAttributes(HTMLAttributes, { class: SECTION_CLASS_NAME }), 0];
  },

  addCommands() {
    return {
      insertLandscapeSection: insertLandscapeSectionCommand,
      unwrapSection: unwrapSectionCommand,
      unwrapAllSections: unwrapAllSectionsCommand,
      deleteSection: deleteSectionCommand,
      setLandscapeMode:
        (isLandscape: boolean) =>
        ({ editor, tr, dispatch }) => {
          editor.storage.section.isLandscape = isLandscape;
          if (dispatch) {
            tr.setMeta('addToHistory', false);
            dispatch(tr);
          }
          return true;
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(SectionNodeComponent, {
      // Ignore ProseMirror events from section-actions (menu button area)
      stopEvent: ({ event }) => {
        const target = event.target as HTMLElement;
        return target.closest('.section-actions') !== null;
      },
    });
  },

  addProseMirrorPlugins() {
    const sectionType = this.type;

    return [
      // Section Manager: tracks section count + auto-merges adjacent sections
      new Plugin({
        key: sectionManagerKey,

        state: {
          init(_, state) {
            return { sectionCount: countSections(state.doc) };
          },

          apply(tr, value, oldState, newState) {
            if (!tr.docChanged) return value;

            // Check for delta meta from commands (O(1) update)
            const meta = tr.getMeta(sectionManagerKey) as TSectionMeta | undefined;
            if (meta?.delta !== undefined) {
              return { sectionCount: Math.max(0, value.sectionCount + meta.delta) };
            }

            // Force recount requested (e.g., after paste or complex operations)
            if (meta?.forceRecount) {
              return { sectionCount: countSections(newState.doc) };
            }

            // Fallback: recount if doc changed but no delta provided.
            // This handles external changes (collaborative editing, etc.)
            // O(k) where k = root children count.
            return { sectionCount: countSections(newState.doc) };
          },
        },

        appendTransaction(transactions, oldState, newState) {
          // Skip if no doc changes
          if (!transactions.some((tr) => tr.docChanged)) return null;

          // Skip if this is already an auto-merge transaction (meta guard)
          if (transactions.some((tr) => tr.getMeta('sectionAutoMerge'))) return null;

          // Local check: only look near the changed positions instead of scanning entire root.
          // Find which root indices were affected by the transactions.
          const affectedIndices = new Set<number>();
          for (const transaction of transactions) {
            transaction.steps.forEach((step) => {
              if (step instanceof ReplaceStep) {
                // Check positions before and after the change
                const fromIndex = getRootIndexAtPos(newState.doc, (step as any).from);
                const toIndex = getRootIndexAtPos(newState.doc, (step as any).to);
                // Include neighbors since adjacency might be created
                for (let i = Math.max(0, fromIndex - 1); i <= Math.min(newState.doc.childCount - 1, toIndex + 1); i++) {
                  affectedIndices.add(i);
                }
              }
            });
          }

          // If no affected indices, skip (shouldn't happen if docChanged)
          if (affectedIndices.size === 0) return null;

          // Check only affected regions for adjacent sections
          const doc = newState.doc;
          const sortedIndices = Array.from(affectedIndices).sort((a, b) => a - b);

          for (const i of sortedIndices) {
            if (i >= doc.childCount - 1) continue;

            const curr = doc.child(i);
            const next = doc.child(i + 1);

            if (curr.type.name === 'section' && next.type.name === 'section') {
              try {
                // Found adjacent sections - merge them
                const tr = newState.tr;

                // Mark as auto-merge to prevent recursive appendTransaction calls
                tr.setMeta('sectionAutoMerge', true);
                // Delta: merging 2 sections into 1 = -1 section
                tr.setMeta(sectionManagerKey, { delta: -1 } as TSectionMeta);

                // Calculate position of first section
                let pos = 0;
                for (let j = 0; j < i; j++) {
                  pos += doc.child(j).nodeSize;
                }

                // Combine content: first section content + second section content
                const combinedContent = curr.content.append(next.content);
                // Keeps the first section's attrs - with null, the merged section lost its id, and a merge during
                // a locked AI accept was saved without one (the id pass skips a read-only editor).
                const mergedSection = sectionType.create(curr.attrs, combinedContent);

                // Replace both sections with merged section using explicit ReplaceStep
                const firstStart = pos;
                const secondEnd = pos + curr.nodeSize + next.nodeSize;
                const step = new ReplaceStep(firstStart, secondEnd, new Slice(Fragment.from(mergedSection), 0, 0));
                tr.step(step);

                return markSystemTr(tr);
              } catch (e) {
                console.error('Section merge failed:', e);
                return null;
              }
            }
          }

          return null;
        },
      }),

      // Paste Handler: unwraps section nodes from clipboard content.
      // When copying content from inside a section and pasting elsewhere,
      // the section wrapper should not be included. This handler extracts
      // the inner content from any pasted sections.
      new Plugin({
        key: new PluginKey('section-paste-handler'),

        props: {
          handlePaste(view, event, slice) {
            // Check if slice contains any section nodes
            let hasSection = false;
            slice.content.forEach((node) => {
              if (node.type.name === 'section') hasSection = true;
            });

            if (!hasSection) return false; // Let TipTap handle normally

            // Transform slice: extract content from section nodes
            const newContent: Parameters<typeof Fragment.from>[0][] = [];
            slice.content.forEach((node) => {
              if (node.type.name === 'section') {
                // Unwrap: add section's children instead of section itself
                node.content.forEach((child) => {
                  newContent.push(child);
                });
              } else {
                newContent.push(node);
              }
            });

            // Create new slice with unwrapped content.
            // Use Slice.maxOpen to compute proper open depths after removing wrapper nodes.
            // This ensures correct join behavior with the target location.
            const fragment = Fragment.fromArray(newContent as any);
            const newSlice = Slice.maxOpen(fragment);

            // Insert the transformed content
            const tr = view.state.tr.replaceSelection(newSlice);
            // Force recount since paste may have complex effects
            tr.setMeta(sectionManagerKey, { forceRecount: true } as TSectionMeta);
            view.dispatch(tr);

            return true; // We handled the paste
          },
        },
      }),
    ];
  },
});

export default SectionNode;
